// IframeViewer.jsx
import React, { useState, useMemo, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

import Loading from '../component/Loading.jsx';
import RichIframe from '../component/RichIframe.jsx';

import { SANDBOX_ALLOW_LIST } from '../resource/popupNotificationConstants.js';
import { EMPTY_ARRAY, NULL_FUNCTION } from '../resource/defaults.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getIsInBrowserMainThread } from '../resource/getJsEnvironment.js';

import { headerHeight, color } from '../style/variables.js';

import replaceTemplate from '../resource/replaceTemplate.js';
import { iframe as iframeDebug } from '../resource/debug.js';

const log = iframeDebug.extend('log:[IframeViewer]');
const errorLog = iframeDebug.extend('error:[IframeViewer]');

const parseJSON = value => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * check if the url is same origin
 * @param {URL} url - the url to check
 * @returns {boolean}
 */
const isSameOrigin = url => {
  if (!getIsInBrowserMainThread()) return false;
  return url.origin.toLowerCase() === window.location.origin.toLowerCase();
};

/**
 * Validates if the current URL matches the allowed URL pattern, including origin and query parameter checks.
 *
 * @param {URL} currentUrl - The current URL object to validate
 * @param {string} allowedUrl - The allowed URL pattern string
 * @param {string} [meId] - Optional user ID for specific value validation
 * @returns {boolean} Returns true if the current URL matches the allowed pattern, false otherwise
 * @param {string} iframeUrlPrefix - The prefix of the iframe URL
 *
 * @description
 * This function performs the following validations:
 * 1. Checks if URL origins match (case-insensitive)
 * 2. If the allowed URL has no query parameters, any URL with matching origin is valid
 * 3. Special handling for Looker Studio's params JSON parameter
 * 4. Validates all query parameters against allowed values
 *
 */
const validateURL = (currentUrl, allowedUrl) => {
  const parsedAllowed = new URL(allowedUrl);

  // Check if origins match (case insensitive)
  if (currentUrl.origin.toLowerCase() !== parsedAllowed.origin.toLowerCase()) {
    return false;
  }

  // Get allowed and current parameters
  const allowedParams = new URLSearchParams(parsedAllowed.search);
  const currentParams = new URLSearchParams(currentUrl.search);

  // If allowed URL has no search params, any URL with matching origin is valid
  if (parsedAllowed.search === '') {
    return true;
  }

  // Handle special params JSON case for Looker Studio
  const allowedParamsJSON = allowedParams.get('params');
  if (allowedParamsJSON) {
    try {
      const allowedJSON = parseJSON(allowedParamsJSON);
      const currentJSON = parseJSON(currentParams.get('params'));

      if (!allowedJSON || !currentJSON) return false;

      return Object.keys(allowedJSON).every(key =>
        Object.prototype.hasOwnProperty.call(currentJSON, key)
      );
    } catch {
      return false;
    }
  }

  // Handle empty parameter values (e.g., lang=)
  for (const [key, allowedValue] of allowedParams.entries()) {
    const currentValue = currentParams.get(key);

    // If allowed value is empty, any value for this parameter is valid
    if (allowedValue === '') {
      continue;
    }

    // If allowed value is specified, current value must match (case insensitive)
    if (
      !currentValue ||
      currentValue.toLowerCase() !== allowedValue.toLowerCase()
    ) {
      return false;
    }
  }

  return true;
};

export const IframeViewer = ({
  meId = '',
  allowURL = EMPTY_ARRAY,
  bypassProxyURL = EMPTY_ARRAY,
  push = NULL_FUNCTION,
  replace = NULL_FUNCTION,
  propsSourceUrl = '',
  iframeUrlPrefix = '',
  iframeStaticUrlPrefix = '',
  shouldShowHeader = true,
  meToken = '',
  tokenWhitelistDomains = EMPTY_ARRAY,
  toggleModal = NULL_FUNCTION,
  apiUrlPrefix = '',
  publicUrlEncryptKeys = '',
  iconBaseUrl = '',
  domainTrackerLabel = '',
  domainTrackerType = '',
  isDomainTrackerFetched = false,
  fetchDomainTrackerResource = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorUrl, setErrorUrl] = useState('');
  const [isIframeReady, setIsIframeReady] = useState(false);
  const { search } = useLocation();
  const iframeSourceRef = useRef(null);
  const iframeOriginRef = useRef(null);

  const sourceUrl = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    let source = propsSourceUrl || searchParams.get('source');

    // if source is a relative path, we need to use iframeStaticUrlPrefix to build the full URL
    if (!source.startsWith('http') && iframeStaticUrlPrefix) {
      source = new URL(source, iframeStaticUrlPrefix).href;
    }

    if (!source) return null;

    try {
      const decodedSource = decodeURIComponent(source);
      const templatedUrl = replaceTemplate({
        string: decodedSource,
        data: { USER_ID: meId || '' },
      });

      const url = new URL(templatedUrl);

      // Merge outer-level Google Form prefill params (entry.*) into source URL
      const outerParams = new URLSearchParams(search);
      outerParams.forEach((value, key) => {
        if (key.startsWith('entry.') && !url.searchParams.has(key)) {
          url.searchParams.set(key, value);
        }
      });

      // Find any matching allowed URL or same origin
      const isAllowed =
        isSameOrigin(url) ||
        allowURL.some(allowedUrl => validateURL(url, allowedUrl));

      if (!isAllowed) {
        errorLog('[iframe] url not in allowURL list', { url: url.href });
        setHasError(true);
        setErrorUrl(url.href);
      }

      return isAllowed ? url : '';
    } catch (error) {
      errorLog('[iframe] failed to parse source url', {
        source,
        error: error.message,
      });
      setHasError(true);
      setErrorUrl(source);
      return null;
    }
  }, [search, meId, allowURL, propsSourceUrl, iframeStaticUrlPrefix]);

  const shouldSendToken = useMemo(() => {
    if (
      !sourceUrl ||
      !tokenWhitelistDomains ||
      tokenWhitelistDomains.length === 0
    )
      return false;

    try {
      const sourceDomain = sourceUrl.hostname.toLowerCase();

      // Check if sourceUrl domain matches any tokenWhitelistDomains domain or is a subdomain
      return tokenWhitelistDomains.some(domain => {
        try {
          const whitelistUrl = new URL(domain);
          const whitelistDomain = whitelistUrl.hostname.toLowerCase();

          return (
            sourceDomain === whitelistDomain ||
            sourceDomain.endsWith('.' + whitelistDomain)
          );
        } catch (_) {
          return false;
        }
      });
    } catch (_) {
      return false;
    }
  }, [sourceUrl, tokenWhitelistDomains]);

  useLayoutEffect(() => {
    if (!iconBaseUrl && domainTrackerLabel && !isDomainTrackerFetched) {
      fetchDomainTrackerResource({
        resourceType: domainTrackerType,
      });
    }
  }, [
    iconBaseUrl,
    domainTrackerLabel,
    isDomainTrackerFetched,
    fetchDomainTrackerResource,
    domainTrackerType,
  ]);

  // Listen for IFRAME_READY - only set up listener once
  useLayoutEffect(() => {
    const onReady = event => {
      if (event?.data?.type !== 'IFRAME_READY') return;

      // Store iframe source and origin for later use
      iframeSourceRef.current = event.source;
      iframeOriginRef.current = event.origin || '*';
      setIsIframeReady(true);
      setIsLoading(false);
    };

    window.addEventListener('message', onReady);
    return () => {
      window.removeEventListener('message', onReady);
    };
  }, []);

  // Send domain config and token when iframe is ready
  useLayoutEffect(() => {
    if (isIframeReady && iframeSourceRef.current && shouldSendToken) {
      const targetOrigin = iframeOriginRef.current || '*';

      // Send domain config first
      if (iconBaseUrl && apiUrlPrefix && publicUrlEncryptKeys) {
        iframeSourceRef.current.postMessage(
          {
            type: 'SET_DOMAIN_CONFIG',
            iconBaseUrl,
            apiUrlPrefix,
            publicUrlEncryptKeys,
          },
          targetOrigin
        );
      }

      // Then send token
      iframeSourceRef.current.postMessage(
        { type: 'SET_TOKEN', token: meToken || '' },
        targetOrigin
      );
    }
  }, [
    isIframeReady,
    shouldSendToken,
    meToken,
    iconBaseUrl,
    apiUrlPrefix,
    publicUrlEncryptKeys,
  ]);

  // Listen to PWA_NAVIGATE, IFRAME_NAVIGATE message to navigate to the target URL
  useLayoutEffect(() => {
    const handleIframeNavigate = event => {
      if (
        ['PWA_NAVIGATE', 'IFRAME_NAVIGATE'].includes(event?.data?.type) &&
        event.data.url
      ) {
        // Close IframeModal before navigation to prevent the modal overlaying the new page content
        toggleModal({
          modalId: 'IframeModal',
          isOpen: false,
        });

        try {
          const targetUrl = new URL(event.data.url);

          const redirectUrl = new URL(
            targetUrl.pathname + targetUrl.search + targetUrl.hash,
            window.location.origin
          );

          push(redirectUrl.pathname + redirectUrl.search + redirectUrl.hash);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            'Failed to parse navigation URL on IframeViewer:',
            event.data.url,
            error
          );
        }
      }
    };

    window.addEventListener('message', handleIframeNavigate);
    return () => {
      window.removeEventListener('message', handleIframeNavigate);
    };
  }, [toggleModal, push]);

  if (!sourceUrl || sourceUrl === null) {
    sourceUrl !== null && replace('/');
    return null;
  }

  const sourceDomain = sourceUrl.host;
  const isClient = typeof window !== 'undefined';

  // Iframe will not able to open on client side render environments, so we need a remote config to change to SSR upstream domains.
  const baseUrl = iframeUrlPrefix || (isClient ? location.origin : '');
  const proxyUrl = baseUrl ? new URL('iframeProxy', baseUrl) : null;

  if (proxyUrl) {
    proxyUrl.searchParams.set('url', sourceUrl.href);
  }

  const shouldBypassProxy =
    bypassProxyURL.includes(sourceDomain) ||
    sourceDomain === new URL(iframeStaticUrlPrefix).host;

  const finalIframeSrc =
    shouldBypassProxy || !proxyUrl ? sourceUrl.href : proxyUrl.href;

  if (shouldBypassProxy) {
    log('bypassing proxy', { sourceDomain, finalSrc: finalIframeSrc });
  } else if (proxyUrl) {
    log('using proxy', {
      sourceDomain,
      proxyUrl: proxyUrl.href,
      iframeUrlPrefix,
    });
  } else {
    errorLog('no proxy available and not in bypass list', {
      sourceDomain,
      iframeUrlPrefix,
    });
  }

  const isWaitingForDomainConfig =
    domainTrackerLabel && !iconBaseUrl && !isDomainTrackerFetched;

  return (
    <StyledIframeViewer shouldShowHeader={shouldShowHeader}>
      {(isLoading || isWaitingForDomainConfig) && !hasError && (
        <LoadingWrapper>
          <Loading size={62} />
        </LoadingWrapper>
      )}
      {hasError && (
        <ErrorContainer>
          <ErrorMessage>
            URL
            {t('invalid_format', {
              ns: TranslationNamespace.SETTINGS,
            })}
          </ErrorMessage>
          <ErrorMessageContent>{errorUrl}</ErrorMessageContent>
        </ErrorContainer>
      )}
      <StyledRichIframe
        src={finalIframeSrc}
        sandbox={SANDBOX_ALLOW_LIST.join(' ')}
        style={{ display: hasError ? 'none' : 'block' }}
        onLoad={() => {
          setIsLoading(false);
        }}
        onError={() => {
          setIsLoading(false);
          setHasError(true);
          setErrorUrl(sourceUrl.href);
        }}
      />
    </StyledIframeViewer>
  );
};

IframeViewer.propTypes = {
  meId: PropTypes.string,
  allowURL: PropTypes.array,
  bypassProxyURL: PropTypes.array,
  push: PropTypes.func,
  replace: PropTypes.func,
  propsSourceUrl: PropTypes.string,
  iframeUrlPrefix: PropTypes.string,
  iframeStaticUrlPrefix: PropTypes.string,
  shouldShowHeader: PropTypes.bool,
  meToken: PropTypes.string,
  tokenWhitelistDomains: PropTypes.array,
  toggleModal: PropTypes.func,
  apiUrlPrefix: PropTypes.string,
  publicUrlEncryptKeys: PropTypes.string,
  iconBaseUrl: PropTypes.string,
  domainTrackerLabel: PropTypes.string,
  domainTrackerType: PropTypes.string,
  isDomainTrackerFetched: PropTypes.bool,
  fetchDomainTrackerResource: PropTypes.func,
};

const StyledIframeViewer = styled.div`
  display: flex;
  width: 100%;
  height: 100dvh;
  position: relative;
  ${({ shouldShowHeader }) =>
    shouldShowHeader && `margin-top: ${headerHeight}px;`}
`;

const LoadingWrapper = styled.div`
  width: 100%;
  position: absolute;
  height: calc(100% - ${headerHeight}px);
  inset: 0;
  z-index: 2;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: ${color.neutral[100]};
  font-size: 16px;
  text-align: center;
  padding: 20px;
`;

const ErrorMessageContent = styled.div`
  color: ${color.neutral[100]};
  font-size: 16px;
  text-align: center;
  padding: 20px;
`;

const StyledRichIframe = styled(RichIframe)`
  width: 100%;
  height: 100%;
  max-width: 768px;
  margin: auto;
`;

export default IframeViewer;
